<template>
  <div class="teilnehmer-feld">
    <div class="d-flex align-items-center mb-1">
      <span class="teilnehmer-feld__label">{{ label }}</span>
      <span v-if="changed" class="teilnehmer-feld__changed-dot ml-2"></span>

      <!-- Status-Icon: grüner Haken bei ausgefülltem Feld (bei gesetzter Nationalität nur wenn deutsch),
           rotes Icon bei fehlendem Wert, gelbe Warnung bei ausgefülltem Feld mit nicht-deutscher Nationalität -->
      <span v-if="showStatus" class="ml-auto d-inline-flex align-items-center">
        <Check v-if="isProvided && (!nationalitaet || isDeutsch)" :size="16" class="text-success" />
        <CircleAlert v-else-if="!isProvided" :size="16" class="text-danger" />
        <span v-else v-b-tooltip.hover title="Achtung! Nicht-deutsche Nationalität." class="d-inline-flex">
          <TriangleAlert :size="16" class="text-warning" />
        </span>
      </span>
    </div>

    <div v-if="readonly" class="teilnehmer-feld__readonly form-control form-control-sm">
      {{ displayValue }}
    </div>
    <template v-else>
      <b-form-input
        v-if="type === 'text'"
        size="sm"
        v-model="editedValue"
        placeholder="Bitte ausfüllen"
        :class="{ 'teilnehmer-feld__input--changed': changed }"
        @input="onTextInput"
        @blur="onTextBlur"
        @keyup.enter="$event.target.blur()"
      />
      <div v-else-if="type === 'select'">
        <AppSelect
          class="teilnehmer-feld__select"
          :label="label"
          :hide-search="!searchable"
          :placeholder="placeholder"
          :options="effectiveOptions"
          :value="value"
          :highlight="changed"
          @input="save"
        />
      </div>
      <b-form-input
        v-else-if="type === 'date'"
        size="sm"
        :value="dateDisplay"
        placeholder="TT.MM.JJJJ"
        inputmode="numeric"
        maxlength="10"
        :class="{ 'teilnehmer-feld__input--changed': changed }"
        @focus="dateFocused = true"
        @input="onDateInput"
        @blur="onDateBlur"
        @keyup.enter="$event.target.blur()"
      />
    </template>

    <div class="mt-1">
      <slot name="hint"></slot>
    </div>
  </div>
</template>

<script>
import AppSelect from '@/components/common/app-select/app-select.vue';
import { isNationalitaetDeutsch } from '@/core/vorgaenge/helpers/teilnehmer-passdaten.utils';
import { Check, CircleAlert, TriangleAlert } from 'lucide-vue';

export default {
  name: 'TeilnehmerFeld',
  components: { AppSelect, Check, CircleAlert, TriangleAlert },
  props: {
    label: { type: String, required: true },
    value: { type: [String, Number], default: null },
    savedValue: { type: [String, Number], default: undefined },
    changed: { type: Boolean, default: false },
    type: { type: String, default: 'text' },
    options: { type: Array, default: () => [] },
    nationalitaet: { type: String, default: null },
    // Optionaler Zusatz, der hinter dem Wert im Feld angezeigt wird (z. B. das Alter beim Geburtsdatum)
    suffix: { type: String, default: '' },
    // Blendet bei Auswahlfeldern das Suchfeld ein
    searchable: { type: Boolean, default: false },
    // Platzhalter des Auswahlfeldes, solange kein Wert gesetzt ist
    placeholder: { type: String, default: 'Bitte wählen' },
    // Steuert die Anzeige des Status-Icons, bei optionalen Feldern ausgeschaltet
    showStatus: { type: Boolean, default: true },
    readonly: { type: Boolean, default: false },
    // Funktion, die den neuen Wert in den Entwurf übernimmt
    saveHandler: { type: Function, required: true },
  },
  data() {
    return {
      editedValue: this.value,
      dateInput: '',
      dateFocused: false,
    };
  },
  created() {
    this.syncDateInput();
  },
  computed: {
    isProvided() {
      const wert = this.savedValue !== undefined ? this.savedValue : this.value;
      return wert != null && wert !== '';
    },
    isDeutsch() {
      return isNationalitaetDeutsch(this.nationalitaet);
    },
    displayValue() {
      if (!this.isProvided) return '-';
      if (this.type === 'date') {
        const formatted = this.$options.filters.date(this.value);
        return this.suffix ? `${formatted} ${this.suffix}` : formatted;
      }
      if (this.type === 'select') {
        return this.options.find(option => option.value === this.value)?.text || this.value;
      }
      return this.value;
    },
    // Anzeige des Datumsfeldes: beim Bearbeiten nur das Datum, sonst inkl. Suffix (z. B. Alter)
    dateDisplay() {
      if (this.dateFocused || !this.dateInput) return this.dateInput;
      return this.suffix ? `${this.dateInput} ${this.suffix}` : this.dateInput;
    },

    effectiveOptions() {
      if (this.isProvided && !this.options.some(option => option.value === this.value)) {
        return [{ value: this.value, text: String(this.value) }, ...this.options];
      }
      return this.options;
    },
  },
  watch: {
    value(newValue) {
      this.editedValue = newValue;
      this.syncDateInput();
    },
  },
  methods: {
    syncDateInput() {
      if (this.type !== 'date') return;
      this.dateInput = this.formatStoredDate(this.value);
    },
    // Wandelt einen gespeicherten Datumswert (YYYY-MM-DD bzw. ISO) in TT.MM.JJJJ um
    formatStoredDate(value) {
      if (!value) return '';
      const match = String(value)
        .substring(0, 10)
        .match(/^(\d{4})-(\d{2})-(\d{2})$/);
      return match ? `${match[3]}.${match[2]}.${match[1]}` : '';
    },
    // Formatiert die Eingabe fortlaufend: Ziffern werden übernommen, Punkte automatisch gesetzt
    onDateInput(rawValue) {
      const digits = rawValue.replace(/\D/g, '').slice(0, 8);
      let formatted = digits;
      if (digits.length > 4) {
        formatted = `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4)}`;
      } else if (digits.length > 2) {
        formatted = `${digits.slice(0, 2)}.${digits.slice(2)}`;
      }
      this.dateInput = formatted;
    },
    // Beim Verlassen wird ein vollständiges, gültiges Datum gespeichert, sonst zurückgesetzt
    onDateBlur() {
      this.dateFocused = false;
      // Ein vorhandener Wert darf nicht gelöscht werden
      // bei leerer Eingabe wird das Feld wieder auf den gespeicherten Wert zurückgesetzt
      if (this.dateInput === '') {
        this.syncDateInput();
        return;
      }
      const isoDate = this.parseGermanDate(this.dateInput);
      if (isoDate) {
        this.save(isoDate);
      } else {
        this.syncDateInput();
      }
    },
    // Prüft TT.MM.JJJJ auf ein reales Datum und liefert es als YYYY-MM-DD zurück (sonst null)
    parseGermanDate(text) {
      const match = text.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
      if (!match) return null;
      const day = Number(match[1]);
      const month = Number(match[2]);
      const year = Number(match[3]);
      const date = new Date(year, month - 1, day);
      const isReal = date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
      return isReal ? `${match[3]}-${match[2]}-${match[1]}` : null;
    },
    // Übernimmt Texteingaben direkt in den Entwurf, damit die Aktionsleiste sofort erscheint
    onTextInput(value) {
      if (value == null || String(value).trim() === '') return;
      this.save(value);
    },
    // Speichert Textfelder beim Verlassen, aber nur wenn sich der Wert tatsächlich geändert hat
    onTextBlur() {
      if (this.editedValue == null || String(this.editedValue).trim() === '') {
        this.editedValue = this.value;
        return;
      }
      if (this.editedValue === this.value) return;
      this.save(this.editedValue);
    },
    save(newValue) {
      if (newValue === this.value) return;
      this.saveHandler(newValue);
    },
  },
};
</script>

<style lang="scss" scoped>
.teilnehmer-feld {
  margin-bottom: 10px;

  :deep(.form-control:not(.app-select__search-input)),
  :deep(.custom-select) {
    height: auto;
    padding: 8px;
    border: 0.5px solid #e5e7eb;
    border-radius: 8px;
    box-shadow: 0px 3px 6px 0px #e5e7eb40;
    color: #101828;
    font-family: Poppins, sans-serif;
    font-weight: 500;
    font-size: 12px;
    line-height: 18px;
    letter-spacing: 0;
  }

  :deep(.form-control:not(.app-select__search-input)::placeholder) {
    color: #80808fbf;
    font-weight: 400;
  }

  :deep(.teilnehmer-feld__input--changed) {
    background: var(--primary-light);
  }

  // Das Auswahlfeld wird an die Breite und Schriftgröße der übrigen Formularfelder angepasst
  .teilnehmer-feld__select {
    width: 100%;
    --app-select-highlight-background: var(--primary-light);

    :deep(.app-select__toggle) {
      min-height: calc(1.35em + 1.1rem + 2px);
      padding: 8px;
      border: 0.5px solid #e5e7eb;
      box-shadow: 0px 3px 6px 0px #e5e7eb40;
    }
    :deep(.app-select__label) {
      font-family: Poppins, sans-serif;
      font-weight: 500;
      font-size: 12px;
      line-height: 18px;
      color: #101828;
    }
    :deep(.app-select__label--placeholder) {
      font-weight: 400;
      color: #80808fbf;
    }
    :deep(.app-select__menu) {
      width: 100%;
      max-width: none;
    }
  }
}
.teilnehmer-feld__changed-dot {
  width: 6px;
  height: 6px;
  flex-shrink: 0;
  border-radius: 50%;
  background: var(--primary);
}
.teilnehmer-feld__label {
  color: #364153d9;
  font-family: Poppins, sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 15px;
  letter-spacing: 0;
}
.teilnehmer-feld__readonly {
  background-color: #f3f6f9;
  min-height: calc(1.35em + 1.1rem + 2px);
}
</style>
